import React, { useState } from 'react';
import { Menu, X, LogOut, Users, ShoppingCart, BarChart3, Settings, Calendar, FileText, Building2, Coffee, ChevronLeft, ChevronRight } from 'lucide-react';

const DashboardSidebar = ({ 
  activeTab, 
  onTabChange, 
  menuItems, 
  dashboardTitle, 
  departmentName,
  onLogout,
  onBackClick,
  backButtonText,
  isCollapsed = false,
  onToggleCollapse
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isDesktopCollapsed = isCollapsed;

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleDesktopSidebar = () => {
    if (onToggleCollapse) {
      onToggleCollapse(!isDesktopCollapsed);
    }
  };

  const handleMenuClick = (tabId) => {
    onTabChange(tabId);
    setIsMobileMenuOpen(false); // Close mobile menu after selection
  };

  return (
    <>
      {/* Mobile Header */}
      <header className="lg:hidden sticky top-0 z-40 flex items-center justify-between border-b border-gray-200 bg-white p-4">
        <div className="flex items-center gap-3">
          <a
            href="/"
            data-app-home-link
            aria-label="Zur Startseite"
            onClick={() => setIsMobileMenuOpen(false)}
            className="w-10 h-10 flex-shrink-0 rounded-full overflow-hidden shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
          >
            <img src="/favicon.png" alt="Logo" className="w-full h-full object-contain" />
          </a>
          <div>
            <h1 className="text-sm font-semibold leading-tight text-gray-950">{dashboardTitle}</h1>
            {departmentName && (
              <p className="text-xs text-gray-500">{departmentName}</p>
            )}
          </div>
        </div>
        <button
          onClick={toggleMobileMenu}
          className="rounded-lg p-2 text-gray-700 hover:bg-gray-100"
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </header>

      {/* Overlay for mobile */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={toggleMobileMenu}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
          fixed top-0 h-screen bg-white shadow-sm z-40
          transition-all duration-300 ease-in-out
          right-0 border-l border-gray-200
          lg:right-auto lg:left-0 lg:border-l-0 lg:border-r
          ${isDesktopCollapsed ? 'w-20' : 'w-64'}
          ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'}
        `}
        style={{ height: '100dvh' }}
      >
        <div className="flex h-full min-h-0 flex-col">
          {/* Logo & Title */}
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain scrollbar-hide p-4">
            <div className={`flex items-center ${isDesktopCollapsed ? 'justify-center' : 'gap-2'} mb-4`}>
              <a
                href="/"
                data-app-home-link
                aria-label="Zur Startseite"
                onClick={() => setIsMobileMenuOpen(false)}
                className="w-10 h-10 flex-shrink-0 rounded-full overflow-hidden shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
              >
                <img src="/favicon.png" alt="Logo" className="w-full h-full object-contain" />
              </a>
              {!isDesktopCollapsed && (
                <div>
                  <h1 className="text-xs font-bold text-gray-900 leading-tight">{dashboardTitle}</h1>
                  {departmentName && (
                    <p className="text-xs text-gray-600">{departmentName}</p>
                  )}
                </div>
              )}
            </div>

            {/* Back Button (optional) */}
            {onBackClick && backButtonText && !isDesktopCollapsed && (
              <button
                onClick={() => {
                  onBackClick();
                  setIsMobileMenuOpen(false);
                }}
                className="w-full flex items-center gap-2 px-4 py-2.5 mb-4 bg-gray-100 text-gray-700 hover:bg-gray-200 hover:text-gray-900 rounded-lg transition-colors text-left"
              >
                <span>←</span>
                <span className="text-sm font-medium">{backButtonText}</span>
              </button>
            )}

            {/* Navigation */}
            <nav className="space-y-1">
              {menuItems.map((item) => {
                const MenuItem = item.href ? 'a' : 'button';
                const menuItemProps = item.href
                  ? {
                      href: item.href,
                      target: '_blank',
                      rel: 'noopener noreferrer',
                      onClick: () => setIsMobileMenuOpen(false),
                    }
                  : {
                      type: 'button',
                      onClick: () => handleMenuClick(item.id),
                    };

                return (
                  <MenuItem
                    key={item.id}
                    {...menuItemProps}
                    className={`
                      flex items-center py-2.5 rounded-lg transition-colors text-left
                      ${isDesktopCollapsed
                        ? 'w-full justify-center px-2'
                        : item.isSubItem
                          ? 'w-[calc(100%-1.25rem)] ml-5 gap-2 px-3 border-l-2 border-gray-200 rounded-l-none'
                          : 'w-full gap-3 px-4'
                      }
                      ${activeTab === item.id
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                      }
                    `}
                    title={isDesktopCollapsed ? item.label : ''}
                  >
                    {item.icon && <item.icon className={`${item.isSubItem ? 'w-4 h-4' : 'w-5 h-5'} flex-shrink-0`} />}
                    {!isDesktopCollapsed && (
                      <span className={item.isSubItem ? 'text-sm font-medium' : 'font-medium'}>{item.label}</span>
                    )}
                  </MenuItem>
                );
              })}

              {/* Logout Button - directly after menu items */}
              <div className="pt-3 mt-3 border-t border-gray-200">
                <button
                  onClick={onLogout}
                  className={`w-full flex items-center ${isDesktopCollapsed ? 'justify-center px-2' : 'gap-3 px-4'} py-2.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors text-left`}
                  title={isDesktopCollapsed ? 'Abmelden' : ''}
                >
                  <LogOut className="w-5 h-5 flex-shrink-0" />
                  {!isDesktopCollapsed && <span className="font-medium">Abmelden</span>}
                </button>
              </div>
            </nav>
          </div>

          {/* Desktop Toggle Button - Floating on right edge */}
          <button
            onClick={toggleDesktopSidebar}
            className={`hidden lg:flex absolute ${onBackClick && backButtonText ? 'top-36' : 'top-20'} -right-3 w-6 h-12 items-center justify-center bg-blue-600 text-white rounded-r-lg shadow-lg hover:bg-blue-700 transition-all z-50`}
            title={isDesktopCollapsed ? 'Menü ausklappen' : 'Menü einklappen'}
          >
            {isDesktopCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </>
  );
};

// Icon mapping helper
export const getIconComponent = (iconName) => {
  const icons = {
    Users,
    ShoppingCart,
    BarChart3,
    Settings,
    Calendar,
    FileText,
    Building2,
    Coffee,
    Menu
  };
  return icons[iconName] || Menu;
};

export default DashboardSidebar;
